import { useState, useEffect } from "react";
import { post } from "../../utils/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../customComponents/ConfirmationModal";
import CarouselModal from "./CarouselModal";

const CarouselSection = () => {
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchCarousels();
  }, []);

  const fetchCarousels = async () => {
    setLoading(true);
    try {
      const res = await post("/cms/carousel/list", {});
      if (res.status) setCarousels(res.data || []);
    } catch {
      toast.error("Failed to load slideshows");
    } finally {
      setLoading(false);
    }
  };

  const moveUp = async (index) => {
    if (index === 0) return;
    const next = [...carousels];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    const reordered = next.map((c, i) => ({ ...c, display_order: i }));
    setCarousels(reordered);
    try {
      await post("/cms/carousel/reorder", {
        items: reordered.map((c) => ({ id: c.id, display_order: c.display_order })),
      });
    } catch {
      toast.error("Reorder failed");
      fetchCarousels();
    }
  };

  const moveDown = async (index) => {
    if (index === carousels.length - 1) return;
    const next = [...carousels];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    const reordered = next.map((c, i) => ({ ...c, display_order: i }));
    setCarousels(reordered);
    try {
      await post("/cms/carousel/reorder", {
        items: reordered.map((c) => ({ id: c.id, display_order: c.display_order })),
      });
    } catch {
      toast.error("Reorder failed");
      fetchCarousels();
    }
  };

  const handleDelete = async () => {
    try {
      await post("/cms/carousel/delete", { id: deleteTarget.id });
      toast.success("Slideshow deleted");
      setDeleteTarget(null);
      fetchCarousels();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (c) => { setEditTarget(c); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); setEditTarget(null); fetchCarousels(); };

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-5 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900">Slideshows</h3>
          <p className="text-xs text-gray-400 mt-0.5">Horizontal strips of trip cards on the app homepage</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-colors"
        >
          + Add
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-3">
        {loading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
        ) : carousels.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">No slideshows yet. Add one to get started.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100 text-xs uppercase tracking-wide">
                <th className="pb-2 font-medium">Title</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {carousels.map((c, i) => (
                <tr key={c.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 text-gray-800 font-medium">{c.title}</td>
                  <td className="py-3">
                    <span className="text-xs bg-blue-50 text-blue-600 rounded-md px-2 py-0.5 font-medium">
                      {c.type}
                    </span>
                  </td>
                  <td className="py-3">
                    <span className={`text-xs font-medium ${c.is_active ? "text-emerald-600" : "text-gray-400"}`}>
                      {c.is_active ? "● Active" : "● Inactive"}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex gap-1 items-center justify-end">
                      <button onClick={() => moveUp(i)} disabled={i === 0}
                        className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-20">
                        <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_upward</span>
                      </button>
                      <button onClick={() => moveDown(i)} disabled={i === carousels.length - 1}
                        className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-20">
                        <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_downward</span>
                      </button>
                      <button onClick={() => openEdit(c)}
                        className="ml-1 px-2 py-1 text-blue-600 hover:text-blue-800 text-xs font-medium">
                        Edit
                      </button>
                      <button onClick={() => setDeleteTarget(c)}
                        className="px-2 py-1 text-red-400 hover:text-red-600 text-xs font-medium">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <CarouselModal
          editTarget={editTarget}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSaved={handleSaved}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Slideshow"
        message={`Delete "${deleteTarget?.title}"? This will also remove all its items.`}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default CarouselSection;
